// # Filename: src/components/navbar/BottomTabBar.jsx
import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LuHouse, LuMedal, LuMessagesSquare, LuTrophy, LuUsers } from "react-icons/lu";

import { useUI } from "../../context/uiContext";
import { useFriends } from "../../context/friendsContext";
import { useDirectMessage } from "../../context/directMessageContext";
import { useInviteContext } from "../../context/inviteContext";

const TABS = [
  { id: "home",        label: "Home",   Icon: LuHouse },
  { id: "friends",     label: "Social", Icon: LuUsers },
  { id: "chat",        label: "Chat",   Icon: LuMessagesSquare },
  { id: "tournaments", label: "Events", Icon: LuTrophy, accent: "amber" },
  { id: "leaderboard", label: "Ranks",  Icon: LuMedal },
];

export default function BottomTabBar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { setSidebarOpen, setDMOpen } = useUI();
  const { pending } = useFriends();
  const { pendingInvites } = useInviteContext();
  const { unreadCounts, groupUnreadCounts } = useDirectMessage();

  const totalUnread = useMemo(
    () =>
      Object.values(unreadCounts || {}).reduce((s, n) => s + Number(n || 0), 0) +
      Object.values(groupUnreadCounts || {}).reduce((s, n) => s + Number(n || 0), 0),
    [unreadCounts, groupUnreadCounts]
  );

  const pendingCount = (pending?.received?.length ?? 0) + (pendingInvites?.length ?? 0);
  const isFocusedFlow =
    /^\/games\/poker\/.+/.test(location.pathname) ||
    location.pathname.startsWith("/lobby/");

  if (isFocusedFlow) return null;

  const getBadge = (id) => {
    if (id === "friends") return pendingCount;
    if (id === "chat")    return totalUnread;
    return 0;
  };

  const isActive = (id) => {
    if (id === "home") return location.pathname === "/";
    if (id === "tournaments") return location.pathname.startsWith("/tournaments");
    if (id === "leaderboard") return location.pathname.startsWith("/leaderboard");
    return false;
  };

  const handleTab = (id) => {
    if (id === "home")        navigate("/");
    if (id === "friends")     setSidebarOpen(true);
    if (id === "chat")        setDMOpen(true);
    if (id === "tournaments") navigate("/tournaments");
    if (id === "leaderboard") navigate("/leaderboard");
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[45] border-t border-border-soft bg-background-app-panel/90 backdrop-blur-xl lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {/* Thin cyan line */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-cyan/40 to-transparent" />

      <div className="flex items-stretch h-16">
        {TABS.map(({ id, label, Icon }) => {
          const active = isActive(id);
          const badge  = getBadge(id);

          return (
            <button
              key={id}
              type="button"
              onClick={() => handleTab(id)}
              className="relative flex flex-1 flex-col items-center justify-center gap-1 transition-colors duration-150 focus:outline-none"
            >
              {/* Active cyan bar */}
              {active && (
                <div className={`absolute left-1/2 top-0 h-[2px] w-8 -translate-x-1/2 ${id === "tournaments" ? "bg-brand-amber shadow-[0_0_24px_rgba(251,191,36,0.35)]" : "bg-brand-cyan shadow-glow-cyan"}`} />
              )}

              <div
                className={`relative grid h-9 w-9 place-items-center rounded-xl border transition ${
                  active
                    ? id === "tournaments"
                      ? "border-brand-amber/30 bg-brand-amber/10 text-brand-amber"
                      : "border-brand-cyan/30 bg-brand-cyan/10 text-brand-cyan"
                    : "border-transparent text-text-muted hover:border-border-soft hover:bg-surface hover:text-text-secondary"
                }`}
              >
                <Icon size={20} strokeWidth={2.1} />
                {badge > 0 && (
                  <span className={`absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold leading-none text-background-app ${id === "tournaments" ? "bg-brand-amber" : "bg-brand-cyan"}`}>
                    {badge > 9 ? "9+" : badge}
                  </span>
                )}
              </div>

              <span className={`text-[9px] font-semibold uppercase tracking-[0.06em] sm:text-[10px] sm:tracking-[0.1em] ${
                active
                  ? id === "tournaments" ? "text-brand-amber" : "text-brand-cyan"
                  : "text-text-muted"
              }`}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
